import { useState, useEffect, useMemo } from "react";
import type { PurchaseOrderFilter as OrderFilter, PurchaseOrdersProps as Props } from "./purchase-order-workspace-types";
import type { PurchaseOrder } from "../domain/models";
import { usePurchaseOrdersData } from "./usePurchaseOrdersData";
import { usePurchaseOrderForm } from "./usePurchaseOrderForm";
import { purchaseOrderGateway } from "../infrastructure/api/purchase-order-gateway";
import { visiblePurchaseOrders, purchaseOrderMetrics } from "./purchase-order-presentation";

export function usePurchaseOrdersWorkspace({
  user,
  notify: onNotify,
  previewKey,
  dataRefreshVersion,
  initialRequestNo,
  onReturnToOffers,
}: Props & { previewKey: string | null; }) {

  const [filter, setFilter] = useState<OrderFilter>("ACTION");
  const [expanded, setExpanded] = useState<string | null>(null);
  const [createdOrder, setCreatedOrder] = useState<PurchaseOrder | null>(null);
  const [recordedDeliveryOrder, setRecordedDeliveryOrder] =
    useState<PurchaseOrder | null>(null);

  const [focusedOrderId, setFocusedOrderId] = useState<string | null>(null);

  const {
    scope,
    notify,
    orders,
    candidates,
    management,
    loading,
    dataLoaded,
    loadError,
    isSpecialist,
    isPlanning,
    isQuality,
    isRolePreview,
    load,
    replaceOrder,
    setCandidates,
  } = usePurchaseOrdersData({
    user,
    notify: onNotify,
    previewKey,
    initialRequestNo,
    dataRefreshVersion,
    onFocusOrder: (order, nextFilter) => { setFilter(nextFilter); setExpanded(order.id); },
    onFocusCandidate: candidate => setDialog({ kind: "create", candidate }),
  });

  const {
    dialog,
    submitting,
    expectedQuantity,
    setExpectedQuantity,
    quantity,
    setQuantity,
    returnQuantity,
    setReturnQuantity,
    returnDisposition,
    setReturnDisposition,
    deliveredAt,
    setDeliveredAt,
    qualityStatus,
    setQualityStatus,
    qualityNote,
    setQualityNote,
    qualityDisposition,
    setQualityDisposition,
    returnReason,
    setReturnReason,
    note,
    setNote,
    deliveryNoteNo,
    setDeliveryNoteNo,
    deliveryDocument,
    setDeliveryDocument,
    dispatchRecipient,
    setDispatchRecipient,
    deliveryAddress,
    setDeliveryAddress,
    operationError,
    setOperationError,
    setDialog,
    openReceipt,
    chooseDeliveryDocument,
    canSubmit,
    submit,
  } = usePurchaseOrderForm({
    scope,
    notify,
    isRolePreview,
    replaceOrder,
    setCandidates,
    onCreated: order => { setCreatedOrder(order); setFilter("ACTION"); setExpanded(order.id); setFocusedOrderId(order.id); },
    onReceived: order => { setRecordedDeliveryOrder(order); setFilter("MOVING"); setExpanded(order.id); setFocusedOrderId(order.id); },
  });

  const documentUrl = purchaseOrderGateway.documentUrl;

  useEffect(() => {
    if (!focusedOrderId) return;
    const frame = window.requestAnimationFrame(() => {
      document
        .querySelector(`[data-purchase-order-id="${focusedOrderId}"]`)
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
    const timer = window.setTimeout(() => setFocusedOrderId(null), 2800);
    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(timer);
    };
  }, [focusedOrderId]);

  const visible = useMemo(
    () =>
      visiblePurchaseOrders(orders, filter, isQuality),
    [filter, isQuality, orders],
  );

  const metrics = useMemo(
    () => purchaseOrderMetrics(orders, candidates, isQuality, isSpecialist),
    [candidates.length, isQuality, isSpecialist, orders],
  );

  return {
    createdOrder,
    initialRequestNo,
    onReturnToOffers,
    expanded,
    setFilter,
    setExpanded,
    setFocusedOrderId,
    recordedDeliveryOrder,
    setRecordedDeliveryOrder,
    management,
    filter,
    isQuality,
    dataLoaded,
    metrics,
    isSpecialist,
    candidates,
    setOperationError,
    setDialog,
    isPlanning,
    loading,
    loadError,
    load,
    visible,
    focusedOrderId,
    setQualityStatus,
    setQualityNote,
    setQualityDisposition,
    documentUrl,
    submitting,
    setDispatchRecipient,
    setDeliveryAddress,
    openReceipt,
    setNote,
    dialog,
    operationError,
    isRolePreview,
    dispatchRecipient,
    deliveryAddress,
    expectedQuantity,
    setExpectedQuantity,
    quantity,
    setQuantity,
    returnQuantity,
    setReturnQuantity,
    deliveredAt,
    setDeliveredAt,
    deliveryNoteNo,
    setDeliveryNoteNo,
    returnDisposition,
    setReturnDisposition,
    returnReason,
    setReturnReason,
    deliveryDocument,
    chooseDeliveryDocument,
    setDeliveryDocument,
    note,
    qualityStatus,
    qualityNote,
    qualityDisposition,
    canSubmit,
    submit,
  };
}
